/**
 * Конфетти — ровно один раз за период, при закрытии оплаченного расчёта.
 *
 * Правило из 17.13: не после каждого действия. Если сыпать конфетти на
 * каждую отметку, к концу недели оно перестанет что-либо значить, а в момент,
 * который действительно стоит отметить, будет выглядеть как шум.
 *
 * Canvas, а не библиотека: тридцать строк против сорока килобайт.
 */

import { useEffect, useRef } from 'react'

import { themeColor } from '@/shared/theme'

/** Цвета берём из темы, а не вписываем числами: палитра одна на приложение. */
function palette(): string[] {
  return [
    themeColor('--color-accent'),
    themeColor('--color-accent-strong'),
    themeColor('--color-warm'),
    themeColor('--color-honey'),
    themeColor('--color-surface'),
  ]
}
const PIECES = 90
const DURATION_MS = 2600

interface Piece {
  x: number
  y: number
  vx: number
  vy: number
  size: number
  color: string
  spin: number
  angle: number
}

export function Confetti({ show, onDone }: { show: boolean; onDone?: () => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    if (!show) return
    const canvas = canvasRef.current
    const context = canvas?.getContext('2d')
    if (!canvas || !context) return

    // Уважаем системную настройку: анимация не должна навязываться тем,
    // кто её отключил.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      onDone?.()
      return
    }

    const ratio = window.devicePixelRatio || 1
    const width = canvas.clientWidth
    const height = canvas.clientHeight
    canvas.width = width * ratio
    canvas.height = height * ratio
    context.scale(ratio, ratio)

    const colors = palette()
    const pieces: Piece[] = Array.from({ length: PIECES }, () => ({
      x: width / 2 + (Math.random() - 0.5) * width * 0.6,
      y: height * 0.35 + (Math.random() - 0.5) * 60,
      vx: (Math.random() - 0.5) * 6,
      vy: -Math.random() * 9 - 3,
      size: 5 + Math.random() * 6,
      color: colors[Math.floor(Math.random() * colors.length)]!,
      spin: (Math.random() - 0.5) * 0.3,
      angle: Math.random() * Math.PI,
    }))

    const started = performance.now()
    let frame = 0

    const tick = (now: number) => {
      const elapsed = now - started
      context.clearRect(0, 0, width, height)

      for (const piece of pieces) {
        piece.vy += 0.28
        piece.x += piece.vx
        piece.y += piece.vy
        piece.angle += piece.spin

        context.save()
        context.translate(piece.x, piece.y)
        context.rotate(piece.angle)
        context.globalAlpha = Math.max(0, 1 - elapsed / DURATION_MS)
        context.fillStyle = piece.color
        context.fillRect(-piece.size / 2, -piece.size / 2, piece.size, piece.size * 0.6)
        context.restore()
      }

      if (elapsed < DURATION_MS) {
        frame = requestAnimationFrame(tick)
      } else {
        context.clearRect(0, 0, width, height)
        onDone?.()
      }
    }

    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [show, onDone])

  if (!show) return null

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      className="pointer-events-none fixed inset-0 z-[80] h-full w-full"
    />
  )
}
